import React from 'react';
import {useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {HubspotIcon} from '@/web-components';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';
import {clientLogoSrc} from '../../directApi';
import {getClientInitials} from './clientsAvatarUtil';
import DeleteClientMutation from '../../mutations/delete_client_mutation';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {createToast} from '../shared/components/toasts/another-toast/toaster';
import Util from '../shared/util/util';
import moreHorizontalIcon from '../../images/components/overlay/navigation-show-more-horizontal-black.svg';

export type ClientRowData = {
	id: string;
	name: string;
	notes: string | null;
	projectCount: number;
	logoId: string | null;
	companyId: string;
	// True when the client has one or more invoices — delete is then disabled (IGNITE-569)
	hasInvoices: boolean;
	// client-level integration fields — drive the per-row "Open in" links + HubSpot row indicator
	hubspotCompanyId: string | null;
	pipedriveOrganizationId: string | null;
	// company-level integration fields (same for all rows) — needed to build the "Open in" URLs
	hubspotAccountId: string | null;
	pipedriveCompanyDomain: string | null;
};

const AVATAR_SIZE = 32;

const AvatarImg = Styled.img`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
`;

// Color matches ContactAvatar on /clients/:id (CompanyViewHeader.tsx)
const AvatarInitials = Styled.div`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 4px;
  background-color: #0004db;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  user-select: none;
`;

// Inner div wrappers are required because <td> does not reliably support display:flex
const NameCellInner = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BorderlessMoreButton = Styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const Td = Styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #e5e5e5;
`;

const NotesTd = Styled(Td)`
  max-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ProjectsTd = Styled(Td)`
  text-align: right;
  white-space: nowrap;
`;

const ActionsTd = Styled(Td)`
  padding: 4px 8px;
`;

const ActionsInner = Styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
`;

type Props = {
	client: ClientRowData;
};

function extractNotesText(notes: string | null): string {
	if (!notes) return '';
	try {
		const parsed = JSON.parse(notes);
		if (parsed && Array.isArray(parsed.blocks) && 'entityMap' in parsed) {
			return parsed.blocks
				.map((b: {text: string}) => b.text)
				.join('\n')
				.trim();
		}
	} catch {
		// not JSON — treat as plain text
	}
	return notes;
}

const ClientsListItem: React.FC<Props> = ({client}) => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	const handleDelete = () => {
		showModal({
			type: MODAL_TYPE.GENERIC_DELETION_WARNING,
			deleteCallback: () => {
				Util.CommitMutation(DeleteClientMutation, {id: client.id, companyId: client.companyId}, () => {
					createToast({
						duration: 5000,
						message: formatMessage({id: 'client.has-been-deleted'}),
					});
				});
			},
		});
	};

	const notesText = extractNotesText(client.notes);

	const avatar = client.logoId ? (
		<AvatarImg crossOrigin="use-credentials" src={clientLogoSrc(client.logoId)} alt="company logo" />
	) : (
		<AvatarInitials aria-hidden="true">{getClientInitials(client.name)}</AvatarInitials>
	);

	return (
		<tr
			data-cy="clients-list-item"
			style={{cursor: 'pointer'}}
			tabIndex={0}
			onClick={() => history.push(`/clients/${client.id}`)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					history.push(`/clients/${client.id}`);
				}
			}}
		>
			<Td>
				<NameCellInner>
					{avatar}
					<span>{client.name}</span>
				</NameCellInner>
			</Td>
			<NotesTd title={notesText || undefined}>{notesText || '-'}</NotesTd>
			<ProjectsTd>{client.projectCount}</ProjectsTd>
			<ActionsTd onClick={e => e.stopPropagation()}>
				<ActionsInner>
					{client.hubspotCompanyId && (
						<span data-testid="hubspot-indicator" title={formatMessage({id: 'clients_page.hubspot_linked'})}>
							<HubspotIcon size={HubspotIcon.SIZE.STANDARD} />
						</span>
					)}
					<DropdownMenu
						trigger={
							<BorderlessMoreButton aria-label="More actions">
								<img src={moreHorizontalIcon} alt="" width={20} height={20} />
							</BorderlessMoreButton>
						}
					>
						<DropdownMenu.Item
							onClick={() => showModal({type: MODAL_TYPE.EDIT_CLIENT_DETAILS, clientId: client.id})}
						>
							{formatMessage({id: 'common.edit'})}
						</DropdownMenu.Item>
						<DropdownMenu.Item
							onClick={handleDelete}
							disabled={client.hasInvoices}
							tooltip={client.hasInvoices ? formatMessage({id: 'settings.client_delete_invoiced'}) : undefined}
							dataCy="delete-client"
						>
							{formatMessage({id: 'common.delete'})}
						</DropdownMenu.Item>
						{client.hubspotCompanyId && client.hubspotAccountId && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`https://app.hubspot.com/contacts/${client.hubspotAccountId}/company/${client.hubspotCompanyId}/`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'HubSpot'})}
							</DropdownMenu.Item>
						)}
						{client.pipedriveOrganizationId && client.pipedriveCompanyDomain && (
							<DropdownMenu.Item
								onClick={() =>
									window.open(
										`${client.pipedriveCompanyDomain}/organization/${client.pipedriveOrganizationId}`,
										'_blank'
									)
								}
							>
								{formatMessage({id: 'settings_clients.open_in'}, {systemName: 'Pipedrive'})}
							</DropdownMenu.Item>
						)}
					</DropdownMenu>
				</ActionsInner>
			</ActionsTd>
		</tr>
	);
};

export default ClientsListItem;
